import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import { MLProject } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectStatus } from '@/utils/enums';

import MainInfoCard from './components/MainInfoCard';
import ResultsTrackingCard from './components/ResultsTrackingCard';
import ScriptsCard from './components/ScriptsCard';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 48px;

  ${PageHeading} {
    margin: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const MLProjectPage = () => {
  const { projectId } = useParams();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: project, isLoading } = useQuery(
    [
      'ml_projects',
      {
        id: projectId,
      },
    ],
    () =>
      api.get<never, MLProject>(`/v1/distribution/ml-projects/${projectId}`),
    {
      retry: 2,
      refetchInterval: 20_000,
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  const isApprovalDisabled =
    project?.status !== MLProjectStatus.DRAFT || !project.translations?.length;

  const onApprove = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Move ${project?.translations?.length} translations to ML Approval`,
      );

      if (!confirmed) {
        return;
      }

      await api.patch(`/v1/distribution/ml-projects/${projectId}/status`, {
        status: MLProjectStatus.PENDING,
      });

      await queryClient.invalidateQueries(['ml_projects']);

      alerts.success('Project successfully moved to ML Approval');
    } catch {
      alerts.error();
    }
  };

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <Header>
        <PageHeading>{project.title}</PageHeading>
        <Button disabled={isApprovalDisabled} onClick={onApprove}>
          Move to ML Approval
        </Button>
      </Header>
      <BackButton />
      <Grid>
        <MainInfoCard project={project} />
        <ScriptsCard project={project} />
        <ResultsTrackingCard
          projectId={project.id}
          translations={project.translations!}
        />
      </Grid>
      <Footer>
        <Button disabled={isApprovalDisabled} onClick={onApprove}>
          Move to ML Approval
        </Button>
      </Footer>
    </>
  );
};

export default MLProjectPage;
